/**
=========================================================
* Annotation Portal React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

// Annotation Portal React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CancelButton from "components/CancelButton";

// Annotation Portal React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavLanguageMenu from "examples/Sidenav/SidenavLanguageMenu";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";

// Annotation Portal React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

import logoutAndRedirect from "utils/logoutHelper";
import UserProfile from "utils/UserProfile";
import { useTranslation } from "hooks/useTranslation";
import useCanHover from "hooks/useCanHover";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const { t } = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname;
  // Logout asks for confirmation first.
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  // Sidebar tooltips are mouse only: on tablets a tap would flash them while navigating.
  const canHover = useCanHover();
  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  const toggleSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  useEffect(() => {
    // Only react when the window crosses the 1200px breakpoint. Tablet browsers fire
    // `resize` whenever the address bar shows/hides on scroll, which must not close an
    // open sidenav.
    let wasSmall = null;

    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      const isSmall = window.innerWidth < 1200;
      if (isSmall === wasSmall) return;
      wasSmall = isSmall;

      setMiniSidenav(dispatch, isSmall);
      setTransparentSidenav(dispatch, isSmall ? false : transparentSidenav);
      setWhiteSidenav(dispatch, isSmall ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch]);

  useEffect(() => {
    if (window.innerWidth < 1200) {
      setMiniSidenav(dispatch, true);
    }
  }, [dispatch, location.pathname]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={location.pathname.startsWith(route)} />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          sx={{ letterSpacing: "0.08em", opacity: 0.6 }}
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox
        pt={2}
        pb={1}
        px={2}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={48}
      >
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>

        {/* Desktop collapse / expand toggle (hamburger) */}
        <Tooltip
          title={
            canHover
              ? miniSidenav
                ? t("navigation.expandSidebar", "Expand sidebar")
                : t("navigation.collapseSidebar", "Collapse sidebar")
              : ""
          }
          placement="right"
        >
          <MDBox
            display={{ xs: "none", xl: "flex" }}
            alignItems="center"
            justifyContent="center"
            width={32}
            height={32}
            flexShrink={0}
            onClick={toggleSidenav}
            sx={{
              cursor: "pointer",
              bgcolor: "rgba(255, 255, 255, 0.08)",
              color: "#fff",
              borderRadius: "10px",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              transition: "background-color 180ms ease, border-color 180ms ease",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.18)",
                borderColor: "rgba(255, 255, 255, 0.28)",
              },
            }}
            aria-label={
              miniSidenav
                ? t("navigation.aria.expandSidebar", "expand sidebar")
                : t("navigation.aria.collapseSidebar", "collapse sidebar")
            }
          >
            <Icon fontSize="small" sx={{ color: "#fff" }}>
              {miniSidenav ? "menu" : "menu_open"}
            </Icon>
          </MDBox>
        </Tooltip>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      {/* The menu scrolls on its own so the profile + Logout below always stay on screen */}
      <List sx={{ px: 0, py: 1, flex: 1, minHeight: 0, overflowY: "auto" }}>{renderRoutes}</List>
      <MDBox
        p={2}
        mt="auto"
        flexShrink={0}
        sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)" }}
      >
        <UserProfile />
        <SidenavLanguageMenu />
        <Tooltip
          title={miniSidenav && canHover ? t("navigation.logout", "Logout") : ""}
          placement="right"
        >
          <MDButton
            component="a"
            rel="noreferrer"
            variant="gradient"
            color={sidenavColor}
            onClick={() => setLogoutConfirmOpen(true)}
            fullWidth
            aria-label={t("navigation.logout", "Logout")}
            sx={{ minWidth: 0 }}
          >
            <Icon sx={{ mr: miniSidenav ? 0 : 1 }}>logout</Icon>
            {!miniSidenav && t("navigation.logout", "Logout")}
          </MDButton>
        </Tooltip>
      </MDBox>
      {/* Centred confirmation: icon, title, question, then two equal-width buttons */}
      <Dialog
        open={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, px: 1, pt: 2, pb: 1 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 0.5 }}>
          <MDBox
            mx="auto"
            mb={1.5}
            width={56}
            height={56}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="50%"
            sx={{ backgroundColor: "#e8f0fe", color: "#1a73e8" }}
          >
            <Icon sx={{ fontSize: "28px !important" }}>logout</Icon>
          </MDBox>
          {/* span: DialogTitle is already an <h2> */}
          <MDTypography variant="h5" component="span" display="block" fontWeight="bold">
            {t("navigation.logout", "Logout")}
          </MDTypography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 1 }}>
          <MDTypography variant="body2" color="text">
            {t("navigation.logoutConfirm", "Are you sure you want to log out?")}
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1.5, "& > :not(:first-of-type)": { ml: 0 } }}>
          <CancelButton fullWidth onClick={() => setLogoutConfirmOpen(false)}>
            {t("navigation.cancel", "Cancel")}
          </CancelButton>
          <MDButton
            variant="gradient"
            color="info"
            fullWidth
            onClick={() => {
              setLogoutConfirmOpen(false);
              logoutAndRedirect();
            }}
          >
            {t("navigation.logout", "Logout")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
